import type { Cents } from "../domain/money";

/**
 * Procurement fulfillment (Phase 9B, spec §26B) — pure, deterministic.
 *
 * Fulfillment is DERIVED from the existing accounting relationships
 * (purchase lines linked to a Product + their allocations to the project);
 * it is never a second manually maintained ledger. The only persisted
 * procurement state is a user disposition (crew-supplied / not-required)
 * because that intent cannot be derived.
 *
 * Quantity attribution rule (deterministic — never guessed):
 *   1. an allocation's own quantity (allocation.quantity_x100) counts when
 *      recorded;
 *   2. else, when the LINE has a quantity and exactly one allocation (all
 *      of it belongs to this project), the line quantity counts;
 *   3. otherwise the purchase contributes DOLLAR evidence with an unknown
 *      quantity — shown, but never silently converted into units.
 * Returns (negative amounts) decrement by the same rule.
 */

export type ProcurementDisposition = "crew-supplied" | "not-required";

export type ProcurementStatus =
  | "need-to-purchase"
  | "partially-purchased"
  | "purchased"
  | "crew-supplied"
  | "not-required"
  | "no-product";

export const PROCUREMENT_STATUS_LABELS: Record<ProcurementStatus, string> = {
  "need-to-purchase": "Need to Purchase",
  "partially-purchased": "Partially Purchased",
  purchased: "Purchased",
  "crew-supplied": "Crew Supplied",
  "not-required": "Not Required",
  "no-product": "Needs Product",
};

/** One allocation of one product-linked purchase line to the project. */
export interface PurchaseEvidenceInput {
  purchaseId: string;
  lineId: string;
  allocationId: string;
  vendor: string;
  date: string;
  /** 'purchase' | 'return' (returns carry negative amounts). */
  kind: string;
  /** Allocation-recorded quantity (×100) in purchase units; null = none. */
  allocationQtyX100: number | null;
  /** Line quantity (×100) in purchase units; null = none. */
  lineQtyX100: number | null;
  /** Number of allocations on the line (for the single-allocation rule). */
  lineAllocationCount: number;
  /** Merchandise cents allocated to the project (net of line discount). */
  allocatedCents: Cents;
  unit: string | null;
}

export interface AttributedEvidence extends PurchaseEvidenceInput {
  /** Units (×100) attributed to the project by the deterministic rule. */
  attributedQtyX100: number | null;
  explanation: string;
}

export interface FulfillmentResult {
  /** Whole purchase units the BOM requires (0 when no product selected). */
  requiredUnits: number;
  purchasedUnitsX100: number;
  /** max(required − purchased, 0). */
  remainingUnitsX100: number;
  /** max(purchased − required, 0) — over-purchase stays visible. */
  excessUnitsX100: number;
  /** Merchandise cents actually allocated (purchases − returns). */
  spentCents: Cents;
  /** Evidence rows whose quantity could not be attributed. */
  unknownQtyCount: number;
  /** A return without quantity can invalidate otherwise complete fulfillment. */
  remainingQuantityUnknown?: boolean;
  status: ProcurementStatus;
  evidence: AttributedEvidence[];
}

export function attributeQuantity(e: PurchaseEvidenceInput): { qtyX100: number | null; explanation: string } {
  if (e.allocationQtyX100 !== null) {
    return { qtyX100: e.allocationQtyX100, explanation: "Quantity recorded on the allocation." };
  }
  if (e.lineQtyX100 !== null && e.lineAllocationCount === 1) {
    const sign = e.kind === "return" || e.allocatedCents < 0 ? -1 : 1;
    return {
      qtyX100: sign * Math.abs(e.lineQtyX100),
      explanation: "Line quantity — the line is allocated entirely to this project.",
    };
  }
  return {
    qtyX100: null,
    explanation:
      e.lineQtyX100 === null
        ? "No quantity recorded on the line or allocation — counted as dollar evidence only."
        : "The line is split across allocations without per-allocation quantities — counted as dollar evidence only.",
  };
}

export function computeFulfillment(input: {
  /** Whole purchase units required (BOM effective purchase units). */
  requiredUnits: number;
  hasProductSelection: boolean;
  disposition: ProcurementDisposition | null;
  evidence: PurchaseEvidenceInput[];
}): FulfillmentResult {
  const evidence: AttributedEvidence[] = input.evidence.map((e) => {
    const a = attributeQuantity(e);
    return { ...e, attributedQtyX100: a.qtyX100, explanation: a.explanation };
  });
  const purchasedUnitsX100 = evidence.reduce((s, e) => s + (e.attributedQtyX100 ?? 0), 0);
  const spentCents = evidence.reduce((s, e) => s + e.allocatedCents, 0);
  const unknownQtyCount = evidence.filter((e) => e.attributedQtyX100 === null).length;
  const remainingQuantityUnknown = evidence.some(e => e.attributedQtyX100 === null && (e.kind === "return" || e.allocatedCents < 0));
  const requiredX100 = input.requiredUnits * 100;
  const remainingUnitsX100 = Math.max(requiredX100 - purchasedUnitsX100, 0);
  const excessUnitsX100 = Math.max(purchasedUnitsX100 - requiredX100, 0);

  let status: ProcurementStatus;
  if (input.disposition === "crew-supplied") status = "crew-supplied";
  else if (input.disposition === "not-required") status = "not-required";
  else if (!input.hasProductSelection && purchasedUnitsX100 <= 0 && evidence.length === 0) status = "no-product";
  else if (requiredX100 > 0 && purchasedUnitsX100 >= requiredX100 && !remainingQuantityUnknown) status = "purchased";
  else if (purchasedUnitsX100 > 0 || (evidence.length > 0 && spentCents !== 0)) status = "partially-purchased";
  else status = "need-to-purchase";

  return {
    requiredUnits: input.requiredUnits,
    purchasedUnitsX100,
    remainingUnitsX100,
    excessUnitsX100,
    spentCents,
    unknownQtyCount,
    remainingQuantityUnknown,
    status,
    evidence,
  };
}

/** Extended purchase cost of an offer: whole units × price per unit. */
export function extendedOfferCostCents(purchaseUnits: number, priceCentsPerUnit: Cents): Cents {
  return purchaseUnits * priceCentsPerUnit;
}
